
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Handshake, Users, Plus, CheckCircle } from 'lucide-react';
import { useTeams } from '@/hooks/useTeams';
import { supabase } from '@/integrations/supabase/client';
import { CreateTeamModal } from './CreateTeamModal';

interface TagButtonProps {
  targetUserId: string;
  targetUserName: string;
  contentType: 'post' | 'cast';
  contentId: string;
}

export const TagButton = ({ targetUserId, targetUserName, contentType, contentId }: TagButtonProps) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const { teams, inviteToTeam, hasTeams, fetchUserTeams, loading } = useTeams({ enabled: isAuthenticated });
  const [isInviting, setIsInviting] = useState(false);
  const [showCreateTeamAlert, setShowCreateTeamAlert] = useState(false);
  const [showCreateTeamModal, setShowCreateTeamModal] = useState(false);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [invitationDetails, setInvitationDetails] = useState<{teamName: string, userName: string, success: boolean} | null>(null);

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setIsAuthenticated(!!user);
    };
    checkAuth();
  }, []);

  const handleInvite = async (teamId: string, teamName: string) => {
    // Validate that we have a proper UUID for the target user
    if (!targetUserId || targetUserId.trim() === '') {
      console.error('❌ Invalid target user ID:', targetUserId);
      return;
    }

    console.log('🎯 Inviting user:', targetUserId, 'to team:', teamId);
    
    setIsInviting(true);
    try {
      const result = await inviteToTeam(
        teamId, 
        targetUserId, 
        `Join our "${teamName}" farming team! We'd love to collaborate with you.`
      );
      
      // Show confirmation dialog regardless of outcome
      setInvitationDetails({
        teamName: teamName,
        userName: targetUserName,
        success: result !== null
      });
      setShowConfirmationDialog(true);
      
    } catch (error) {
      console.error('❌ Error in handleInvite:', error);
      // Show error confirmation
      setInvitationDetails({
        teamName: teamName,
        userName: targetUserName,
        success: false
      });
      setShowConfirmationDialog(true);
    } finally {
      setIsInviting(false);
    }
  };

  const handleTeamCreated = () => {
    fetchUserTeams();
  };

  // Show the create team alert only when clicking the button and user has no teams
  const handleButtonClick = () => {
    // Wait for loading to complete before checking teams
    if (loading) {
      return;
    }
    
    if (!hasTeams) {
      setShowCreateTeamAlert(true);
    }
  };

  // Show loading state while teams are being fetched
  if (loading) {
    return (
      <Button 
        variant="ghost" 
        size="sm"
        disabled
        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
      >
        <Handshake size={16} className="mr-1" />
        tAG
      </Button>
    );
  }

  // If user has no teams after loading is complete, show the create team flow
  if (!hasTeams) {
    return (
      <>
        <Button 
          variant="ghost" 
          size="sm"
          onClick={handleButtonClick}
          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
        >
          <Handshake size={16} className="mr-1" />
          tAG
        </Button>

        <AlertDialog open={showCreateTeamAlert} onOpenChange={setShowCreateTeamAlert}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Create a Team First</AlertDialogTitle>
              <AlertDialogDescription>
                You need to create a team before you can invite other farmers to collaborate. 
                Would you like to create your first team now?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Maybe Later</AlertDialogCancel>
              <AlertDialogAction 
                onClick={() => {
                  setShowCreateTeamAlert(false);
                  setShowCreateTeamModal(true);
                }}
              >
                <Plus size={16} className="mr-2" />
                Create Team
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <CreateTeamModal
          open={showCreateTeamModal}
          onOpenChange={setShowCreateTeamModal}
          onTeamCreated={handleTeamCreated}
        />
      </>
    );
  }

  // If user has teams, show the dropdown
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="ghost" 
            size="sm"
            disabled={isInviting}
            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
          >
            <Handshake size={16} className="mr-1" />
            tAG
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <div className="px-2 py-1.5 text-sm font-medium text-gray-700 border-b">
            Invite {targetUserName} to team:
          </div>
          {teams.map((team) => (
            <DropdownMenuItem
              key={team.id}
              onClick={() => handleInvite(team.id, team.name)}
              className="cursor-pointer"
            >
              <Users size={14} className="mr-2" />
              {team.name}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirmationDialog} onOpenChange={setShowConfirmationDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              {invitationDetails?.success ? (
                <>
                  <CheckCircle className="text-green-600" size={24} />
                  Invitation Sent!
                </>
              ) : (
                <>
                  <Handshake className="text-orange-600" size={24} />
                  Cannot Send Invitation
                </>
              )}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {invitationDetails && (
                <>
                  {invitationDetails.success ? (
                    <>
                      You've successfully invited <strong>{invitationDetails.userName}</strong> to join your 
                      "<strong>{invitationDetails.teamName}</strong>" team. They'll receive a message with the invitation details.
                    </>
                  ) : (
                    <>
                      You cannot invite <strong>{invitationDetails.userName}</strong> to your team because 
                      this appears to be your own content. You can only invite other farmers to collaborate with you.
                    </>
                  )}
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setShowConfirmationDialog(false)}>
              Got it!
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
